import {
  Tag,
  Toggle,
  SkeletonText,
  InlineNotification,
  Loading,
} from '@carbon/react';
import type { CarbonIconType } from '@carbon/icons-react';
import { Notification, Email, Mobile, Application } from '@carbon/icons-react';
import {
  useNotifPrefs,
  useSetNotifPref,
  useEnableChannel,
  useDisableChannel,
} from '@sphere/notification/data-access';
import {
  CHANNEL_LABELS,
  TOPIC_LABELS,
  NotificationChannel,
  NotificationTopic,
  NOTIFICATION_TOPICS_IMPLEMENTED_IN_ORCHESTRATOR,
  NOTIFICATION_CHANNELS_WITH_ORCHESTRATOR_DELIVERY,
} from '@sphere/notification/domain';
import { ErrorView } from '@sphere/shared/ui/design-system';
import { toast } from 'sonner';
import { SettingsSection, SettingsSectionItem } from './components';
import shared from './settings-pages.shared.module.scss';
import styles from './NotificationsPage.module.scss';

const CHANNELS = NOTIFICATION_CHANNELS_WITH_ORCHESTRATOR_DELIVERY;

const TOPICS = NOTIFICATION_TOPICS_IMPLEMENTED_IN_ORCHESTRATOR.filter(
  (topic): topic is NotificationTopic =>
    TOPIC_LABELS[topic] != null
);

const CHANNEL_ICONS: Record<NotificationChannel, CarbonIconType> = {
  [NotificationChannel.EMAIL]: Email,
  [NotificationChannel.PUSH]: Notification,
  [NotificationChannel.SMS]: Mobile,
  [NotificationChannel.IN_APP]: Application,
  [NotificationChannel.INAPP]: Application,
};

export default function NotificationsPage() {
  const { data: notifPrefs, isLoading, error, refetch } = useNotifPrefs();
  const setNotifPref = useSetNotifPref();
  const enableChannel = useEnableChannel();
  const disableChannel = useDisableChannel();

  const isEnabled = (
    channel: NotificationChannel,
    topic: NotificationTopic
  ) => {
    if (!Array.isArray(notifPrefs)) return true;
    const pref = notifPrefs.find(
      (p) => p.channel === channel && p.topic === topic
    );
    return pref?.enabled ?? true;
  };

  const isChannelFullyEnabled = (channel: NotificationChannel) => {
    if (!Array.isArray(notifPrefs)) return true;
    const channelPrefs = notifPrefs.filter((p) => p.channel === channel);
    if (channelPrefs.length === 0) return true;
    return channelPrefs.every((p) => p.enabled);
  };

  const handleToggle = async (
    channel: NotificationChannel,
    topic: NotificationTopic,
    enabled: boolean
  ) => {
    try {
      await setNotifPref.mutateAsync({ channel, topic, enabled });
      toast.success(
        enabled ? 'Notification activée' : 'Notification désactivée'
      );
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleChannelToggle = async (
    channel: NotificationChannel,
    enabled: boolean
  ) => {
    try {
      if (enabled) {
        await enableChannel.mutateAsync(channel);
        toast.success(`Canal ${CHANNEL_LABELS[channel].label} activé`);
      } else {
        await disableChannel.mutateAsync(channel);
        toast.success(`Canal ${CHANNEL_LABELS[channel].label} désactivé`);
      }
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const isPending =
    setNotifPref.isPending ||
    enableChannel.isPending ||
    disableChannel.isPending;

  if (isLoading) {
    return <NotificationsSkeleton />;
  }

  if (error) {
    return (
      <div className={shared.page}>
        <div className={shared.statePanel}>
          <ErrorView
            title="Erreur de chargement"
            message="Impossible de charger les préférences de notification."
            error={error as Error}
            onRetry={() => refetch()}
            variant="card"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={shared.page}>
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="Notifications disponibles"
        subtitle="Seuls les types déjà pris en charge par la plateforme (activités d’apprentissage, rappels / résumés pédagogiques, système) sont listés. D’autres sujets seront ajoutés au fur et à mesure des canaux branchés."
      />

      <SettingsSection
        title="Canaux de notification"
        description="E-mail et notifications push (mobile / navigateur)"
        layout="grid"
      >
        {CHANNELS.map((channel) => {
          const Icon = CHANNEL_ICONS[channel];
          const isFullyEnabled = isChannelFullyEnabled(channel);

          return (
            <SettingsSectionItem
              key={channel}
              icon={Icon}
              label={CHANNEL_LABELS[channel].label}
              sublabel={isFullyEnabled ? 'Activé' : 'Partiellement activé'}
              action={
                <Toggle
                  id={`channel-${channel}-toggle`}
                  labelText={`Activer tout le canal ${CHANNEL_LABELS[channel].label}`}
                  hideLabel
                  size="sm"
                  toggled={isFullyEnabled}
                  onToggle={(checked) =>
                    handleChannelToggle(channel, checked)
                  }
                  disabled={isPending}
                />
              }
            />
          );
        })}
      </SettingsSection>

      <SettingsSection
        title="Préférences détaillées"
        description="Par type de notification et par canal (e-mail ou push)"
      >
        <div className={styles.matrixWrap}>
          <div
            className={styles.preferenceMatrix}
            role="group"
            aria-label="Préférences de notification par sujet et par canal"
          >
            <div className={styles.matrixHeaderRow}>
              <div className={styles.matrixCorner} />
              {CHANNELS.map((channel) => {
                const Icon = CHANNEL_ICONS[channel];
                return (
                  <div key={channel} className={styles.matrixColHead}>
                    <Icon size={16} aria-hidden />
                    <span>{CHANNEL_LABELS[channel].label}</span>
                  </div>
                );
              })}
            </div>
            {TOPICS.map((topic) => (
              <div key={topic} className={styles.matrixRow}>
                <div className={styles.matrixTopic}>
                  <div className={styles.topicTitleRow}>
                    <h4 className={styles.topicTitle}>
                      {TOPIC_LABELS[topic]?.label}
                    </h4>
                    {topic === NotificationTopic.SYSTEM && (
                      <Tag type="gray">Important</Tag>
                    )}
                  </div>
                  <p className={styles.topicDescription}>
                    {TOPIC_LABELS[topic]?.description}
                  </p>
                </div>
                {CHANNELS.map((channel) => {
                  const enabled = isEnabled(channel, topic);
                  const topicLabel = TOPIC_LABELS[topic]?.label ?? topic;
                  const channelLabel = CHANNEL_LABELS[channel]?.label ?? channel;
                  const aria = `${topicLabel}, ${channelLabel}`;

                  return (
                    <div
                      key={`${topic}-${channel}`}
                      className={styles.matrixToggleCell}
                    >
                      <Toggle
                        id={`pref-${topic}-${channel}`}
                        labelText={aria}
                        hideLabel
                        size="sm"
                        toggled={enabled}
                        onToggle={(checked) =>
                          handleToggle(channel, topic, checked)
                        }
                        disabled={
                          isPending || topic === NotificationTopic.SYSTEM
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </SettingsSection>

      {isPending && (
        <div className={shared.savingIndicator}>
          <Loading small withOverlay={false} />
          Enregistrement...
        </div>
      )}
    </div>
  );
}

function NotificationsSkeleton() {
  return (
    <div className={shared.page}>
      <SettingsSection title=" " description=" ">
        <SkeletonText width="12rem" heading />
        <SkeletonText width="16rem" />
        <div
          className="cds--skeleton cds--skeleton--text"
          style={{ height: '5rem' }}
        />
        <div
          className="cds--skeleton cds--skeleton--text"
          style={{ height: '5rem' }}
        />
        <div
          className="cds--skeleton cds--skeleton--text"
          style={{ height: '5rem' }}
        />
      </SettingsSection>
    </div>
  );
}
